/**
 * The content cards of the room detail page (D4c2): Belegung,
 * Zimmerdetails, Verknüpfungen.
 *
 * They share one shell (`DetailCard`) with the report detail page's
 * cards, so border, radius, padding and heading rhythm cannot drift
 * apart — a person moving between a report and a room should not notice
 * a change of card language.
 *
 * All presentational: data in, markup out, no fetching and no state.
 * The page owns loading and errors.
 *
 * **No occupancy is derived here.** `planning_state`, `free_from_date`,
 * `current_occupancy` and `next_occupancy` arrive finished from the
 * backend's canonical rule; these cards choose words and lay them out.
 */
import React from "react";
import { Link } from "react-router-dom";
import {
  CalendarClock,
  ChevronRight,
  DoorOpen,
  Layers,
  Maximize2,
  Ruler,
  Tag,
  Wallet,
} from "lucide-react";

import { isActiveRoom } from "../../../utils/unitOccupancyStatus";
import { DetailCard } from "../workReports/WorkReportDetailCards";
import EntityLinkRows from "../entityLinks/EntityLinkRows";
import {
  availabilityText,
  monthsText,
  occupancyPeriodText,
  occupantName,
  planningStateLabel,
  planningStateTone,
  roomPriceText,
  roomSizeText,
} from "./roomDetailHelpers";

/** Same tone vocabulary the unit pages already use for occupancy, so
 * "belegt" is the same green everywhere. */
const TONE_CLASSES = {
  green:
    "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
  blue: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400",
  slate:
    "border-border bg-muted/60 text-foreground dark:border-[#1e2130] dark:bg-[#0d0f14] dark:text-[#c5cbe0]",
};

/**
 * The room's general status — ACTIVITY FIRST, planning second.
 *
 * `planning_state` is computed by the backend from tenancies and knows
 * nothing about whether the room is still part of the rentable
 * inventory. A stood-down room therefore announced itself as "Frei" (it
 * has no tenancy) or "Belegt" (a historic one that still runs), on both
 * mounts of this badge — the header and the top of Belegung.
 *
 * `isActiveRoom` is the only activity truth here, the same one the room
 * table, the room-status overview and the room map use. NOT
 * `room.status`: the wire sends `status = getattr(r, "status", None) or
 * "Frei"`, so a retired room's `status` reads "Frei". This page never
 * showed that column anyway (see `RoomFactsCard` below), and this does
 * not start.
 *
 * ONLY THIS LABEL CHANGES. The availability line beside it, the current
 * and next occupancy, the periods and every stored field stay exactly as
 * they were — an inactive room that is still let keeps its tenant on
 * screen. We correct what the room is presented as, not what happened
 * in it.
 *
 * The neutral tone is the one this component already falls back to, so a
 * retired room cannot read as free, reserved or occupied.
 */
export function PlanningStateBadge({ room }) {
  const retired = !isActiveRoom(room);
  const tone = retired
    ? TONE_CLASSES.slate
    : TONE_CLASSES[planningStateTone(room)] || TONE_CLASSES.slate;
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tone}`}
    >
      {retired ? "Inaktiv" : planningStateLabel(room)}
    </span>
  );
}

/** One labelled fact. `<dt>`/`<dd>` so assistive tech reads it as the
 * pair it is; the icon is decorative and never the only carrier of
 * meaning. */
function FactRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border/60 py-2.5 last:border-b-0 dark:border-[#1e2130]/70">
      <dt className="flex min-w-0 shrink-0 items-center gap-2 text-[12px] text-muted-foreground dark:text-[#4a5070]">
        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {label}
      </dt>
      <dd className="m-0 min-w-0 break-words text-right text-[13px] font-medium text-foreground dark:text-[#c5cbe0]">
        {value}
      </dd>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Belegung
// ──────────────────────────────────────────────────────────────────────

/**
 * One occupancy: who, for how long, and a link to the person.
 *
 * The tenant is a real link — `/admin/tenants/:id` exists. The TENANCY
 * is not: tenancies live as cards on the tenant page and have no route
 * of their own, the same refusal the link rows make elsewhere. So the
 * period is text and the name is the way in.
 */
function OccupancyBlock({ occupancy, kind, emptyText }) {
  if (!occupancy) {
    return (
      <p className="m-0 text-[13px] italic text-muted-foreground dark:text-[#4a5070]">
        {emptyText}
      </p>
    );
  }
  const name = occupantName(occupancy);
  const period = occupancyPeriodText(occupancy, kind);
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {occupancy.tenant_id ? (
          <Link
            to={`/admin/tenants/${occupancy.tenant_id}`}
            className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-[8px] border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[12px] font-medium text-amber-700 transition-colors hover:bg-amber-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-amber-400"
          >
            <span className="truncate">{name}</span>
            <ChevronRight className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          </Link>
        ) : (
          <span className="text-[13px] font-medium text-foreground dark:text-[#c5cbe0]">
            {name}
          </span>
        )}
        <p className="m-0 mt-1.5 text-[12px] text-muted-foreground dark:text-[#94a3b8]">
          {period}
        </p>
      </div>
    </div>
  );
}

/**
 * Who is in the room now, who comes next, and when it is free.
 *
 * The three answer one question together, which is why they share a
 * card: "belegt bis 30.09." and "frei ab 01.10." only make sense as a
 * pair, and splitting them is how they start contradicting each other.
 */
export function RoomOccupancyCard({ room }) {
  return (
    <DetailCard headingId="room-occupancy-heading" title="Belegung">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <PlanningStateBadge room={room} />
        <span className="inline-flex items-center gap-1.5 text-[12px] text-muted-foreground dark:text-[#94a3b8]">
          <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {availabilityText(room)}
        </span>
      </div>

      <h3 className="m-0 mb-2 text-[12px] font-semibold uppercase tracking-[0.6px] text-muted-foreground dark:text-[#4a5070]">
        Aktuell
      </h3>
      <OccupancyBlock
        occupancy={room?.current_occupancy}
        kind="current"
        emptyText="Dieses Zimmer ist aktuell nicht belegt."
      />

      <h3 className="m-0 mb-2 mt-5 text-[12px] font-semibold uppercase tracking-[0.6px] text-muted-foreground dark:text-[#4a5070]">
        Nachmieter
      </h3>
      <OccupancyBlock
        occupancy={room?.next_occupancy}
        kind="next"
        emptyText="Für dieses Zimmer ist kein Nachmieter reserviert."
      />
    </DetailCard>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Zimmerdetails
// ──────────────────────────────────────────────────────────────────────

/**
 * The room's own stored fields.
 *
 * `status` is the room's legacy stored column and is deliberately NOT
 * shown beside the derived occupancy: the two can disagree (the backend
 * says so in as many words), and putting them side by side would ask a
 * person to referee a conflict the system has already resolved in favour
 * of `planning_state`.
 */
export function RoomFactsCard({ room }) {
  const size = roomSizeText(room);
  const price = roomPriceText(room);
  return (
    <DetailCard headingId="room-facts-heading" title="Zimmerdetails">
      <dl className="m-0">
        <FactRow icon={DoorOpen} label="Name" value={room?.name || "—"} />
        <FactRow
          icon={Layers}
          label="Etage"
          value={room?.floor == null ? "—" : String(room.floor)}
        />
        <FactRow icon={Ruler} label="Fläche" value={size || "—"} />
        {/* Planned target rent (Soll). The actual rent lives on the
            tenancy — the label says "geplant" so the two are never read
            as the same number. */}
        <FactRow icon={Wallet} label="Miete geplant" value={price || "—"} />
        <FactRow
          icon={Maximize2}
          label="Mindestmietdauer"
          value={monthsText(room?.default_minimum_stay_months)}
        />
        <FactRow
          icon={Tag}
          label="Kündigungsfrist"
          value={monthsText(room?.default_notice_period_months)}
        />
        <FactRow
          icon={DoorOpen}
          label="Aktiv"
          value={room?.is_active === false ? "Nein" : "Ja"}
        />
      </dl>
    </DetailCard>
  );
}

// ──────────────────────────────────────────────────────────────────────
// Verknüpfungen
// ──────────────────────────────────────────────────────────────────────

/**
 * Where the room lives: its unit, and that unit's property.
 *
 * Both are structural parents, not links someone chose — a room is
 * always in exactly one unit. They are shown because they are how a
 * person navigates out of here, and they use the SAME rows as the task
 * and report pages so a unit reads the same on all three.
 */
export function RoomParentsCard({ room }) {
  const entries = [
    {
      key: "property",
      label: "Liegenschaft",
      value: room?.property_id
        ? room?.property_display_name || "Liegenschaft nicht verfügbar"
        : null,
      to: room?.property_id ? `/admin/properties/${room.property_id}` : null,
    },
    {
      key: "unit",
      label: "Unit",
      value: room?.unit_id
        ? room?.unit_display_name || "Unit nicht verfügbar"
        : null,
      to: room?.unit_id ? `/admin/units/${room.unit_id}` : null,
    },
  ];
  return (
    <DetailCard headingId="room-parents-heading" title="Verknüpfungen">
      <EntityLinkRows entries={entries} />
    </DetailCard>
  );
}
